/**
 * Chatwoot Conversations Service
 *
 * Handles all conversation-related endpoints.
 * All endpoints require account_id.
 *
 * @see ConversationsApi.md
 */

import { BaseChatwootService } from "../core/base-chatwoot-service";
import {
  ConversationListParams,
  ConversationListResponse,
  ConversationDetailResponse,
  ConversationStatusToggleResponse,
  ChatwootConversation,
  ConversationStatus,
  ConversationAssigneeType,
  ConversationSortBy,
  ConversationType,
  type ConversationLifecycleView,
  type ConversationExpirationPolicy,
  type ConversationExpirationPolicyInput,
} from "../types/conversations.types";
import type { ConversationLabels } from "../types/labels.types";

/**
 * ConversationFilterRequest payload
 * Used for filtering conversations with custom filters (POST endpoint)
 */
export interface ConversationFilterRequest {
  payload?: {
    q?: string;
    status?: string[];
    inbox_id?: number[];
    labels?: string[];
    assignee_type?: string;
    team_id?: number[];
    [key: string]: any;
  };
  page?: number;
}

/**
 * ChatwootConversationsService
 * Handles conversation-related API operations
 *
 * @example
 * ```typescript
 * const conversationsService = new ChatwootConversationsService();
 *
 * // List conversations
 * const conversations = await conversationsService.listConversations(accountId, { page: 1 });
 *
 * // Get conversation details
 * const conversation = await conversationsService.getConversationDetails(accountId, conversationId);
 * ```
 */
export class ChatwootConversationsService extends BaseChatwootService {
  constructor(baseUrl?: string) {
    super(baseUrl);
  }
  /**
   * List Conversations
   * GET /api/accounts/conversations
   *
   * Supported query parameters:
   * - status: Filter by conversation status (all, open, resolved, pending, snoozed)
   * - assignee_type: Filter by assignee (me, unassigned, all, assigned)
   * - conversation_type: Pre-defined filter type (unattended, mention, participating)
   * - sort_by: Sort order (last_activity_at_desc, last_activity_at_asc, created_at_desc, created_at_asc)
   * - page: Page number for pagination (default: 1)
   * - q: Search query string (inline filtering)
   * - inbox_id: Filter by specific inbox ID
   * - team_id: Filter by specific team ID
   * - labels: Filter by conversation labels (can be array)
   *
   * Note: For comprehensive search across all conversations, use searchConversations() instead.
   *
   * @param accountId - The numeric ID of the account (optional, not used in URL but kept for compatibility)
   * @param params - Query parameters (page, status, inbox_id, etc.)
   * @returns Promise with conversation list
   *
   * @example
   * ```typescript
   * // Get open conversations assigned to me for a specific inbox
   * const conversations = await service.listConversations(accountId, {
   *   status: ConversationStatus.OPEN,
   *   assignee_type: ConversationAssigneeType.ME,
   *   sort_by: ConversationSortBy.LAST_ACTIVITY_AT_DESC,
   *   page: 1,
   *   inbox_id: 123
   * });
   *
   * // Get all conversations with labels
   * const conversations = await service.listConversations(accountId, {
   *   labels: ["urgent", "follow-up"]
   * });
   * ```
   */
  async listConversations(
    accountId: number | null,
    params?: ConversationListParams
  ): Promise<ConversationListResponse> {
    const queryParams = new URLSearchParams();

    if (params) {
      // Handle status - can be single value or array (legacy compat)
      if (params.status !== undefined) {
        if (Array.isArray(params.status)) {
          params.status.forEach((status) => queryParams.append("status", status));
        } else {
          queryParams.append("status", params.status);
        }
      }

      // Phase 3: filter by workflow status definition id
      if (params.status_id !== undefined && params.status_id !== null && params.status_id !== "") {
        queryParams.append("status_id", String(params.status_id));
      }

      // Handle assignee_type (single value)
      if (params.assignee_type !== undefined) {
        queryParams.append("assignee_type", params.assignee_type);
      }

      // Handle conversation_type (single value)
      // This is a pre-defined filter that translates to specific backend queries
      if (params.conversation_type !== undefined) {
        queryParams.append("conversation_type", params.conversation_type);
      }

      // Handle sort_by
      if (params.sort_by !== undefined) {
        queryParams.append("sort_by", String(params.sort_by));
      }

      // Handle page
      if (params.page !== undefined) {
        queryParams.append("page", String(params.page));
      }

      // Handle search query (inline filtering within current filters)
      if (params.q !== undefined && params.q !== "") {
        queryParams.append("q", params.q);
      }

      // Handle inbox_id (single value in query params)
      if (params.inbox_id !== undefined) {
        queryParams.append("inbox_id", String(params.inbox_id));
      }

      // Handle team_id
      if (params.team_id !== undefined) {
        queryParams.append("team_id", String(params.team_id));
      }

      // Handle labels - can be single string or array
      if (params.labels !== undefined) {
        if (Array.isArray(params.labels)) {
          params.labels.forEach((label) => queryParams.append("labels", label));
        } else {
          queryParams.append("labels", params.labels);
        }
      }

      // Handle limit
      if (params.limit !== undefined) {
        queryParams.append("limit", String(params.limit));
      }

      // Archive tab: expired tickets only (active tabs omit the param)
      if (params.lifecycle === "archived") {
        queryParams.append("lifecycle", "archived");
      }
    }

    const queryString = queryParams.toString();
    // Use /api/accounts/conversations (without /v1)
    const url = `/api/accounts/conversations${queryString ? `?${queryString}` : ""}`;
    return await this.get<ConversationListResponse>(url);
  }

  /**
   * Search Conversations (Dedicated Search Endpoint)
   * GET /api/accounts/conversations/search
   *
   * This endpoint performs a comprehensive search across all conversations,
   * searching in contact names, emails, and message content.
   * It's more powerful than the inline 'q' parameter in listConversations.
   *
   * @param accountId - The numeric ID of the account (not used in URL, but kept for consistency)
   * @param query - Search query string
   * @param page - Page number for pagination (default: 1)
   * @returns Promise with search results
   *
   * @example
   * ```typescript
   * // Search for conversations containing "john"
   * const results = await service.searchConversations(accountId, "john", 1);
   * ```
   */
  async searchConversations(
    accountId: number | null,
    query: string,
    page: number = 1,
    lifecycle?: ConversationLifecycleView
  ): Promise<ConversationListResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append("q", query);
    queryParams.append("page", String(page));
    if (lifecycle === "archived") {
      queryParams.append("lifecycle", "archived");
    }

    const queryString = queryParams.toString();
    const url = `/api/accounts/conversations/search${queryString ? `?${queryString}` : ""}`;
    return await this.get<ConversationListResponse>(url);
  }

  /**
   * Get Conversation Details
   * GET /api/accounts/conversations/{conversation_id}
   *
   * @param accountId - The numeric ID of the account (not used in URL, but kept for API compatibility)
   * @param conversationId - Conversation ID (numeric Chatwoot or UUID for native FrontDesk)
   * @returns Promise with conversation details
   */
  async getConversationDetails(
    accountId: number,
    conversationId: number | string
  ): Promise<ConversationDetailResponse> {
    // Use /api/accounts/conversations/{id} (without /v1)
    return await this.get<ConversationDetailResponse>(
      `/api/accounts/conversations/${encodeURIComponent(String(conversationId))}`
    );
  }

  /**
   * Filter Conversations
   * POST /api/accounts/conversations/filter
   * Note: Does NOT use account ID in URL path (authenticated user's account is used)
   *
   * @param accountId - The numeric ID of the account (kept for compatibility but not used in URL)
   * @param filterRequest - Filter criteria and pagination
   * @returns Promise with filtered conversation list
   */
  async filterConversations(accountId: number, filterRequest: ConversationFilterRequest): Promise<ConversationListResponse> {
    return await this.post<ConversationListResponse>("/api/accounts/conversations/filter", filterRequest);
  }

  /**
   * Create New Conversation
   * POST /api/accounts/conversations
   * Note: Does NOT use account ID in URL path (authenticated user's account is used)
   *
   * @param accountId - The numeric ID of the account (kept for compatibility but not used in URL)
   * @param payload - Conversation creation payload
   * @returns Promise with created conversation
   */
  async createConversation(accountId: number, payload: any): Promise<ConversationDetailResponse> {
    return await this.post<ConversationDetailResponse>("/api/accounts/conversations", payload);
  }

  /**
   * Update Conversation
   * PATCH /api/accounts/conversations/{conversation_id}
   * Note: Does NOT use account ID in URL path (authenticated user's account is used)
   *
   * @param accountId - The numeric ID of the account (kept for compatibility but not used in URL)
   * @param conversationId - The numeric ID of the conversation
   * @param payload - Update payload
   * @returns Promise with updated conversation
   */
  async updateConversation(
    accountId: number,
    conversationId: number | string,
    payload: any
  ): Promise<ConversationDetailResponse> {
    return await this.patch<ConversationDetailResponse>(
      `/api/accounts/conversations/${encodeURIComponent(String(conversationId))}`,
      payload
    );
  }

  /**
   * Expire (archive) a ticket. Future customer messages start a new conversation.
   * POST /api/accounts/conversations/{conversation_id}/expire
   */
  async expireConversation(conversationId: number | string): Promise<ConversationDetailResponse> {
    return await this.post<ConversationDetailResponse>(
      `/api/accounts/conversations/${encodeURIComponent(String(conversationId))}/expire`,
      {}
    );
  }

  /**
   * Re-activate an archived ticket. 409 ACTIVATION_CONFLICT when a newer active
   * ticket owns the same communication path (`active_conversation_id`).
   * POST /api/accounts/conversations/{conversation_id}/activate
   */
  async activateConversation(conversationId: number | string): Promise<ConversationDetailResponse> {
    return await this.post<ConversationDetailResponse>(
      `/api/accounts/conversations/${encodeURIComponent(String(conversationId))}/activate`,
      {}
    );
  }

  /**
   * Print-house conversation expiration policy (disabled by default).
   * GET /api/accounts/conversation_expiration
   */
  async getExpirationPolicy(): Promise<ConversationExpirationPolicy> {
    const response = await this.get<{ payload: ConversationExpirationPolicy }>(
      "/api/accounts/conversation_expiration"
    );
    return response.payload;
  }

  /**
   * Replace the expiration policy. Rules with an `id` are updated, others created, omitted deleted.
   * 400 bodies carry `{ code, message, rule_index? }`.
   * PUT /api/accounts/conversation_expiration
   */
  async saveExpirationPolicy(
    policy: ConversationExpirationPolicyInput
  ): Promise<ConversationExpirationPolicy> {
    const response = await this.put<{ payload: ConversationExpirationPolicy }>(
      "/api/accounts/conversation_expiration",
      policy
    );
    return response.payload;
  }

  /**
   * Mark Conversation as Read
   * POST /api/accounts/conversations/{conversation_id}/update_last_seen
   *
   * Updates the agent's last seen timestamp, marking the conversation as read.
   *
   * @param accountId - The numeric ID of the account (kept for compatibility)
   * @param conversationId - The numeric ID of the conversation
   * @returns Promise<void>
   *
   * @example
   * ```typescript
   * await service.markConversationAsRead(accountId, 123);
   * ```
   */
  async markConversationAsRead(
    accountId: number,
    conversationId: number | string
  ): Promise<void> {
    return await this.post<void>(
      `/api/accounts/conversations/${encodeURIComponent(String(conversationId))}/update_last_seen`,
      {}
    );
  }

  /**
   * Mark Conversation as Unread
   * POST /api/accounts/conversations/{conversation_id}/unread
   *
   * Marks the conversation as unread for the current agent.
   *
   * @param accountId - The numeric ID of the account (kept for compatibility)
   * @param conversationId - The numeric ID of the conversation
   * @returns Promise<void>
   *
   * @example
   * ```typescript
   * await service.markConversationAsUnread(accountId, 123);
   * ```
   */
  async markConversationAsUnread(
    accountId: number,
    conversationId: number | string
  ): Promise<void> {
    return await this.post<void>(
      `/api/accounts/conversations/${encodeURIComponent(String(conversationId))}/unread`,
      {}
    );
  }

  /**
   * Update Conversation Status
   * POST /api/accounts/conversations/{conversation_id}/toggle_status
   *
   * Changes the conversation status (open, resolved, pending, snoozed).
   * For snooze, include snoozed_until timestamp.
   * Note: Does NOT use account ID in URL path (authenticated user's account is used)
   *
   * @param accountId - The numeric ID of the account (kept for compatibility but not used in URL)
   * @param conversationId - The numeric ID of the conversation
   * @param payload - Status change payload
   * @returns Promise with updated conversation
   *
   * @example
   * ```typescript
   * // Resolve conversation
   * await service.updateConversationStatus(accountId, 123, { status: 'resolved' });
   *
   * // Snooze conversation until tomorrow
   * await service.updateConversationStatus(accountId, 123, {
   *   status: 'snoozed',
   *   snoozed_until: '2024-12-25T10:00:00Z'
   * });
   * ```
   */
  async updateConversationStatus(
    accountId: number,
    conversationId: number | string,
    payload: {
      status?: "open" | "resolved" | "pending" | "snoozed";
      /** Preferred: status definition publicId */
      status_id?: number | string;
      /** ISO string, unix seconds, or null for until-next-reply */
      snoozed_until?: string | number | null;
    }
  ): Promise<ConversationStatusToggleResponse> {
    return await this.post<ConversationStatusToggleResponse>(
      `/api/accounts/conversations/${encodeURIComponent(String(conversationId))}/toggle_status`,
      payload
    );
  }

  /**
   * @deprecated Use updateConversationStatus instead
   * Toggle Conversation Status
   * POST /api/accounts/conversations/{conversation_id}/toggle_status
   *
   * @param accountId - The numeric ID of the account (kept for compatibility but not used in URL)
   * @param conversationId - The numeric ID of the conversation
   * @returns Promise with toggled conversation
   */
  async toggleConversationStatus(
    accountId: number,
    conversationId: number | string
  ): Promise<ConversationDetailResponse> {
    return await this.post<ConversationDetailResponse>(
      `/api/accounts/conversations/${encodeURIComponent(String(conversationId))}/toggle_status`
    );
  }

  /**
   * Update Conversation Priority
   * POST /api/accounts/conversations/{conversation_id}/toggle_priority
   *
   * Sets the conversation priority level.
   * Note: Does NOT use account ID in URL path (authenticated user's account is used)
   *
   * @param accountId - The numeric ID of the account (kept for compatibility but not used in URL)
   * @param conversationId - The numeric ID of the conversation
   * @param payload - Priority change payload
   * @returns Promise with updated conversation
   *
   * @example
   * ```typescript
   * // Set to urgent priority
   * await service.updateConversationPriority(accountId, 123, { priority: 'urgent' });
   *
   * // Remove priority
   * await service.updateConversationPriority(accountId, 123, { priority: null });
   * ```
   */
  async updateConversationPriority(
    accountId: number,
    conversationId: number | string,
    payload: {
      priority: 'urgent' | 'high' | 'medium' | 'low' | null;
    }
  ): Promise<ConversationDetailResponse> {
    return await this.post<ConversationDetailResponse>(
      `/api/accounts/conversations/${encodeURIComponent(String(conversationId))}/toggle_priority`,
      payload
    );
  }

  /**
   * @deprecated Use updateConversationPriority instead
   * Toggle Conversation Priority
   * POST /api/accounts/conversations/{conversation_id}/toggle_priority
   *
   * @param accountId - The numeric ID of the account (kept for compatibility but not used in URL)
   * @param conversationId - The numeric ID of the conversation
   * @returns Promise with toggled conversation
   */
  async toggleConversationPriority(
    accountId: number,
    conversationId: number | string
  ): Promise<ConversationDetailResponse> {
    return await this.post<ConversationDetailResponse>(
      `/api/accounts/conversations/${encodeURIComponent(String(conversationId))}/toggle_priority`
    );
  }

  /**
   * Assign Agent to Conversation
   * POST /api/accounts/conversations/{conversation_id}/assignments?assignee_id={agent_id}
   *
   * Assigns or unassigns an agent to the conversation.
   * Note: Does NOT use account ID in URL path (authenticated user's account is used)
   *
   * @param accountId - The numeric ID of the account (kept for compatibility but not used in URL)
   * @param conversationId - The numeric ID of the conversation
   * @param assigneeId - The agent ID to assign, or null to unassign
   * @returns Promise with assignment result
   *
   * @example
   * ```typescript
   * // Assign agent with ID 42
   * await service.assignAgent(accountId, 123, 42);
   *
   * // Unassign agent
   * await service.assignAgent(accountId, 123, null);
   * ```
   */
  async assignAgent(
    accountId: number,
    conversationId: number | string,
    assigneeId: number | string | null
  ): Promise<ChatwootConversation> {
    const url = `/api/accounts/conversations/${conversationId}/assignments`;
    // Unassign must send assignee_id: null in the body — an empty POST is
    // rejected by native FrontDesk ("assignee_id or team_id is required").
    if (assigneeId !== null && assigneeId !== undefined && assigneeId !== "") {
      return await this.post<ChatwootConversation>(
        `${url}?assignee_id=${encodeURIComponent(String(assigneeId))}`,
        {}
      );
    }
    return await this.post<ChatwootConversation>(url, { assignee_id: null });
  }

  /**
   * Assign Team to Conversation
   * POST /api/accounts/conversations/{conversation_id}/assignments
   *
   * Assigns or unassigns a team to the conversation.
   * Note: Does NOT use account ID in URL path (authenticated user's account is used)
   *
   * @param accountId - The numeric ID of the account (kept for compatibility but not used in URL)
   * @param conversationId - The numeric ID of the conversation
   * @param teamId - The team ID to assign, or null to unassign
   * @returns Promise with assignment result
   *
   * @example
   * ```typescript
   * // Assign team with ID 5
   * await service.assignTeam(accountId, 123, 5);
   *
   * // Unassign team
   * await service.assignTeam(accountId, 123, null);
   * ```
   */
  async assignTeam(
    accountId: number,
    conversationId: number | string,
    teamId: number | null
  ): Promise<ChatwootConversation> {
    const normalizedTeamId =
      teamId === null || teamId === undefined || teamId === 0 ? null : teamId;
    return await this.post<ChatwootConversation>(
      `/api/accounts/conversations/${conversationId}/assignments`,
      { team_id: normalizedTeamId }
    );
  }

  /**
   * SET assigned agents for a conversation (multi-agent).
   * POST /api/accounts/conversations/{id}/assignments/agents
   * Body: { agent_ids: string[] }
   *
   * Empty array unassigns all agents.
   */
  async setAssignedAgents(
    accountId: number,
    conversationId: number | string,
    agentIds: Array<number | string>
  ): Promise<ChatwootConversation> {
    return await this.post<ChatwootConversation>(
      `/api/accounts/conversations/${conversationId}/assignments/agents`,
      { agent_ids: agentIds.map(String) }
    );
  }

  /**
   * Delete Conversation (Admin Only)
   * DELETE /api/accounts/conversations/{conversation_id}
   *
   * Soft-deletes a conversation via background job.
   * This endpoint should only be accessible to admin users.
   *
   * @param accountId - The numeric ID of the account (kept for compatibility)
   * @param conversationId - The numeric ID of the conversation
   * @returns Promise<void>
   *
   * @example
   * ```typescript
   * // Delete conversation (admin only)
   * await service.deleteConversation(accountId, 123);
   * ```
   */
  async deleteConversation(
    accountId: number,
    conversationId: number | string
  ): Promise<void> {
    return await this.delete<void>(
      `/api/accounts/conversations/${encodeURIComponent(String(conversationId))}`
    );
  }

  /**
   * Add Labels to Conversation
   * POST /api/accounts/conversations/{conversation_id}/labels
   * Note: Does NOT use account ID in URL path (authenticated user's account is used)
   *
   * @param accountId - The numeric ID of the account (kept for compatibility but not used in URL)
   * @param conversationId - The numeric ID of the conversation
   * @param labels - Array of label names
   * @returns Promise with updated labels
   */
  async addConversationLabels(
    accountId: number,
    conversationId: number | string,
    labels: string[]
  ): Promise<ConversationLabels> {
    return await this.post<ConversationLabels>(
      `/api/accounts/conversations/${encodeURIComponent(String(conversationId))}/labels`,
      { labels }
    );
  }

  /**
   * List Conversation Labels
   * GET /api/accounts/conversations/{conversation_id}/labels
   * Note: Does NOT use account ID in URL path (authenticated user's account is used)
   *
   * @param accountId - The numeric ID of the account (kept for compatibility but not used in URL)
   * @param conversationId - Conversation ID (numeric Chatwoot or UUID for native FrontDesk)
   * @returns Promise with conversation labels
   */
  async listConversationLabels(
    accountId: number,
    conversationId: number | string
  ): Promise<ConversationLabels> {
    return await this.get<ConversationLabels>(
      `/api/accounts/conversations/${encodeURIComponent(String(conversationId))}/labels`
    );
  }

  /**
   * Update Conversation Custom Attributes
   * POST /api/accounts/conversations/{conversation_id}/custom_attributes
   * Note: Does NOT use account ID in URL path (authenticated user's account is used)
   *
   * @param accountId - The numeric ID of the account (kept for compatibility but not used in URL)
   * @param conversationId - Conversation ID (numeric Chatwoot or UUID for native FrontDesk)
   * @param customAttributes - Custom attributes object
   * @returns Promise with updated conversation
   */
  async updateConversationCustomAttributes(
    accountId: number,
    conversationId: number | string,
    customAttributes: Record<string, unknown>
  ): Promise<ChatwootConversation> {
    return await this.post<ChatwootConversation>(
      `/api/accounts/conversations/${encodeURIComponent(String(conversationId))}/custom_attributes`,
      { custom_attributes: customAttributes }
    );
  }

  /**
   * Get Conversation Counts/Meta
   * GET /api/accounts/conversations/meta
   * Note: Does NOT use account ID in URL path (authenticated user's account is used)
   *
   * @param accountId - The numeric ID of the account (kept for compatibility but not used in URL)
   * @returns Promise with conversation metadata (counts, etc.)
   */
  async getConversationMeta(accountId: number): Promise<{ mine_count?: number; unassigned_count?: number; all_count?: number }> {
    return await this.get<{ mine_count?: number; unassigned_count?: number; all_count?: number }>("/api/accounts/conversations/meta");
  }

  /**
   * Phase 4: list communication paths for the path picker / multi-path send.
   * GET /api/accounts/conversations/{id}/communication-paths
   */
  async listCommunicationPaths(
    accountId: number,
    conversationId: number | string
  ): Promise<
    import("../types/conversations.types").CommunicationPathsPayload
  > {
    const response = await this.get<
      import("../types/conversations.types").CommunicationPathsResponse
    >(
      `/api/accounts/conversations/${encodeURIComponent(
        String(conversationId)
      )}/communication-paths`
    );
    const payload = response?.payload ?? response?.data?.payload;
    if (!payload) {
      return {
        canonical_conversation_id: String(conversationId),
        default_path_conversation_id: null,
        paths: [],
      };
    }
    return payload;
  }

  /**
   * Automation Auto Send: hub ledger state of the ticket's latest customer message.
   * GET /api/accounts/conversations/{id}/auto-send-status
   */
  async getAutoSendState(
    conversationId: number | string
  ): Promise<import("../types/conversations.types").AutoSendStatePayload | null> {
    const response = await this.get<
      import("../types/conversations.types").AutoSendStateResponse
    >(
      `/api/accounts/conversations/${encodeURIComponent(
        String(conversationId)
      )}/auto-send-status`
    );
    return response?.payload ?? response?.data?.payload ?? null;
  }

  /**
   * Phase 1: list same-client merge candidates for the open conversation.
   * GET /api/accounts/conversations/{id}/merge-candidates?q=
   *
   * Direction: candidates are conversations that can be absorbed INTO `conversationId`.
   */
  async listMergeCandidates(
    accountId: number,
    conversationId: number | string,
    params?: { q?: string }
  ): Promise<import("../types/conversations.types").MergeCandidate[]> {
    const query = new URLSearchParams();
    if (params?.q?.trim()) {
      query.set("q", params.q.trim());
    }
    const qs = query.toString();
    const path = `/api/accounts/conversations/${encodeURIComponent(
      String(conversationId)
    )}/merge-candidates${qs ? `?${qs}` : ""}`;

    const response = await this.get<
      import("../types/conversations.types").MergeCandidatesResponse
    >(path);
    const payload = response?.payload ?? response?.data?.payload;
    return Array.isArray(payload) ? payload : [];
  }

  /**
   * Manual AI Assist input: latest customer (incoming, non-private) message on the ticket
   * timeline plus bounded prior history, resolved from stored message direction.
   * GET /api/accounts/conversations/{id}/ai_assist_input
   */
  async getAiAssistInput(
    conversationId: number | string
  ): Promise<import("../types/conversations.types").AiAssistInput> {
    const response = await this.get<
      import("../types/conversations.types").AiAssistInputResponse
    >(
      `/api/accounts/conversations/${encodeURIComponent(
        String(conversationId)
      )}/ai_assist_input`
    );
    const payload = response?.payload ?? response?.data?.payload;
    if (!payload) {
      throw new Error("AI Assist input response missing payload");
    }
    return payload;
  }

  /**
   * Phase 1: merge target (B) into source conversation (A = URL id).
   * POST /api/accounts/conversations/{id}/merge
   * Body: { target_conversation_id: B }
   *
   * Result: A/root stays canonical; B/root is absorbed.
   */
  async mergeConversations(
    accountId: number,
    sourceConversationId: number | string,
    targetConversationId: number | string
  ): Promise<import("../types/conversations.types").MergeConversationsResult> {
    const response = await this.post<
      import("../types/conversations.types").MergeConversationsResponse
    >(
      `/api/accounts/conversations/${encodeURIComponent(
        String(sourceConversationId)
      )}/merge`,
      { target_conversation_id: String(targetConversationId) }
    );
    const payload = response?.payload ?? response?.data?.payload;
    if (!payload?.canonical_conversation_id) {
      throw new Error("Merge response missing canonical_conversation_id");
    }
    return payload;
  }

  /**
   * Detach absorbed member B from canonical ticket A.
   * POST /api/accounts/conversations/{id}/unmerge
   * Body: { member_conversation_id: B }
   */
  async unmergeConversations(
    accountId: number,
    canonicalConversationId: number | string,
    memberConversationId: number | string
  ): Promise<import("../types/conversations.types").UnmergeConversationsResult> {
    const response = await this.post<
      import("../types/conversations.types").UnmergeConversationsResponse
    >(
      `/api/accounts/conversations/${encodeURIComponent(
        String(canonicalConversationId)
      )}/unmerge`,
      { member_conversation_id: String(memberConversationId) }
    );
    const payload = response?.payload ?? response?.data?.payload;
    if (
      !payload?.canonical_conversation_id ||
      !payload?.unmerged_conversation_id
    ) {
      throw new Error("Unmerge response missing conversation ids");
    }
    return payload;
  }
}
